import NBType, { InferType } from '@netbase/nb-type'
import AnalysisType from '../enums/AnalysisType'
import AnnotationDisplayMode from '../enums/AnnotationDisplayMode'
import AuthorSchema from './AuthorSchema'
import AuthorTypeGroup from '../enums/AuthorTypeGroup'
import CVLogoSchema from './CVLogoSchema'
import ComparisonMode from '../enums/ComparisonMode'
import DayHourSchema from './DayHourSchema'
import Followers from '../enums/Followers'
import Gender from '../enums/Gender'
import GeoDisplaySchema from './GeoDisplaySchema'
import GeoFencingSchema from './GeoFencingSchema'
import Interest from '../enums/Interest'
import Language from '../enums/Language'
import LxnxFilterSchema from './LxnxFilterSchema'
import MeasureType from '../enums/MeasureType'
import MeasurementFilterSchema from './MeasurementFilterSchema'
import MediaType from '../enums/MediaType'
import MetricTimeUnit from '../enums/MetricTimeUnit'
import OrganicPaidPostType from '../enums/OrganicPaidPostType'
import OwnedEarned from '../enums/OwnedEarned'
import PostSchema from './PostSchema'
import PostTypeGroup from '../enums/PostTypeGroup'
import Precision from '../enums/Precision'
import Profession from '../enums/Profession'
import ScopedStringListSchema from './ScopedStringListSchema'
import Sentiment from '../enums/Sentiment'
import SentimentDriverSchema from './SentimentDriverSchema'
import SocialWebChannelSchema from './SocialWebChannelSchema'
import Source from '../enums/Source'
import ThemeInfoListSchema from './ThemeInfoListSchema'
import ThemeInfoSchema from './ThemeInfoSchema'
import TimeInterval from '../enums/TimeInterval'
import TopicInfoSchema from './TopicInfoSchema'
import VerifiedUsers from '../enums/VerifiedUsers'
import WidgetType from '../enums/WidgetType'

/**
 * @typedef {import('../enums/AnalysisType').AnalysisType} AnalysisType
 * @typedef {import('../enums/AnnotationDisplayMode').AnnotationDisplayMode} AnnotationDisplayMode
 * @typedef {import('./AuthorSchema').Author} Author
 * @typedef {import('../enums/AuthorTypeGroup').AuthorTypeGroup} AuthorTypeGroup
 * @typedef {import('./CVLogoSchema').CVLogo} CVLogo
 * @typedef {import('../enums/ComparisonMode').ComparisonMode} ComparisonMode
 * @typedef {import('./DayHourSchema').DayHour} DayHour
 * @typedef {import('../enums/Followers').Followers} Followers
 * @typedef {import('../enums/Gender').Gender} Gender
 * @typedef {import('./GeoDisplaySchema').GeoDisplay} GeoDisplay
 * @typedef {import('./GeoFencingSchema').GeoFencing} GeoFencing
 * @typedef {import('../enums/Interest').Interest} Interest
 * @typedef {import('../enums/Language').Language} Language
 * @typedef {import('./LxnxFilterSchema').LxnxFilter} LxnxFilter
 * @typedef {import('../enums/MeasureType').MeasureType} MeasureType
 * @typedef {import('./MeasurementFilterSchema').MeasurementFilter} MeasurementFilter
 * @typedef {import('../enums/MediaType').MediaType} MediaType
 * @typedef {import('../enums/MetricTimeUnit').MetricTimeUnit} MetricTimeUnit
 * @typedef {import('../enums/OrganicPaidPostType').OrganicPaidPostType} OrganicPaidPostType
 * @typedef {import('../enums/OwnedEarned').OwnedEarned} OwnedEarned
 * @typedef {import('./PostSchema').Post} Post
 * @typedef {import('../enums/PostTypeGroup').PostTypeGroup} PostTypeGroup
 * @typedef {import('../enums/Precision').Precision} Precision
 * @typedef {import('../enums/Profession').Profession} Profession
 * @typedef {import('./ScopedStringListSchema').ScopedStringList} ScopedStringList
 * @typedef {import('../enums/Sentiment').Sentiment} Sentiment
 * @typedef {import('./SentimentDriverSchema').SentimentDriver} SentimentDriver
 * @typedef {import('./SocialWebChannelSchema').SocialWebChannel} SocialWebChannel
 * @typedef {import('../enums/Source').Source} Source
 * @typedef {import('./ThemeInfoListSchema').ThemeInfoList} ThemeInfoList
 * @typedef {import('./ThemeInfoSchema').ThemeInfo} ThemeInfo
 * @typedef {import('../enums/TimeInterval').TimeInterval} TimeInterval
 * @typedef {import('./TopicInfoSchema').TopicInfo} TopicInfo
 * @typedef {import('../enums/VerifiedUsers').VerifiedUsers} VerifiedUsers
 * @typedef {import('../enums/WidgetType').WidgetType} WidgetType
 */

/**
 * @typedef {object} SocialWebTimelineComparisonWidgetSchema
 * @property {AnalysisType} analysisType
 * @property {AnnotationDisplayMode[]} annotations
 * @property {AuthorTypeGroup[]} authorTypeGroups
 * @property {ComparisonMode} comparisonMode
 * @property {CVLogo[]} cvLogos
 * @property {DayHour[]} dayHours
 * @property {number} endDatetime
 * @property {Author[]} excludedAuthors
 * @property {string[][]} excludedBioTexts
 * @property {SocialWebChannel[]} excludedChannels
 * @property {string[]} excludedDomains
 * @property {string[]} excludedEmojis
 * @property {string[][]} excludedGeographies
 * @property {GeoDisplay[][]} excludedGeographiesSummary
 * @property {Interest[]} excludedInterests
 * @property {Language[]} excludedLanguages
 * @property {Post[][]} excludedPosts
 * @property {string[][]} excludedProductHierarchy
 * @property {Profession[]} excludedProfessions
 * @property {SentimentDriver} excludedSentimentDriver
 * @property {ScopedStringList[]} excludedTerms
 * @property {string[][]} excludedUserTags
 * @property {Followers[]} followers
 * @property {Gender[]} genders
 * @property {GeoFencing[][]} geoFencings
 * @property {Author[]} includedAuthors
 * @property {string[][]} includedBioTexts
 * @property {SocialWebChannel[]} includedChannels
 * @property {string[]} includedDomains
 * @property {string[]} includedEmojis
 * @property {string[][]} includedGeographies
 * @property {GeoDisplay[][]} includedGeographiesSummary
 * @property {Interest[]} includedInterests
 * @property {Language[]} includedLanguages
 * @property {Post[][]} includedPosts
 * @property {string[][]} includedProductHierarchy
 * @property {Profession[]} includedProfessions
 * @property {SentimentDriver} includedSentimentDriver
 * @property {ScopedStringList[]} includedTerms
 * @property {string[][]} includedUserTags
 * @property {LxnxFilter} lxnxFilter
 * @property {MeasureType[]} measureTypes
 * @property {MediaType[]} mediaTypes
 * @property {string[]} metricNames
 * @property {string} name
 * @property {MeasurementFilter[]} netPromoterScores
 * @property {OrganicPaidPostType[]} organicPaidPostTypes
 * @property {OwnedEarned[]} ownedEarned
 * @property {AnalysisType} parentAnalysisType
 * @property {ThemeInfo[]} pivotThemeInfos
 * @property {number} posX
 * @property {number} posY
 * @property {PostTypeGroup[]} postTypeGroups
 * @property {Precision} precision
 * @property {string[]} primaryTerms
 * @property {string[]} removedInsights
 * @property {TopicInfo} selectedTopic
 * @property {Sentiment[]} sentiments
 * @property {number} sizeX
 * @property {number} sizeY
 * @property {Source[]} sources
 * @property {MeasurementFilter[]} starRatings
 * @property {number} startDatetime
 * @property {MeasurementFilter[]} surveyScores
 * @property {ThemeInfoList[]} themeInfos
 * @property {TimeInterval} timeInterval
 * @property {MetricTimeUnit} timeUnit
 * @property {TopicInfo[]} topicInfos
 * @property {WidgetType} type
 * @property {VerifiedUsers[]} verifiedUsers
 * @property {number} widgetId
 */
const SocialWebTimelineComparisonWidgetSchema = NBType.object().shape(
  'SocialWebTimelineComparisonWidgetSchema',
  {
    /* @prop */
    analysisType: NBType.enum(AnalysisType),
    /* @prop */
    annotations: NBType.array().of(NBType.enum(AnnotationDisplayMode)),
    /* @prop */
    authorTypeGroups: NBType.array().of(NBType.enum(AuthorTypeGroup)),
    /* @prop */
    comparisonMode: NBType.enum(ComparisonMode),
    /* @prop */
    cvLogos: NBType.array().of(CVLogoSchema),
    /* @prop */
    dayHours: NBType.array().of(DayHourSchema),
    /* @prop */
    endDatetime: NBType.number(),
    /* @prop */
    excludedAuthors: NBType.array().of(AuthorSchema),
    /* @prop */
    excludedBioTexts: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    excludedChannels: NBType.array().of(SocialWebChannelSchema),
    /* @prop */
    excludedDomains: NBType.array().of(NBType.string()),
    /* @prop */
    excludedEmojis: NBType.array().of(NBType.string()).nullable(),
    /* @prop */
    excludedGeographies: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    excludedGeographiesSummary: NBType.array().of(NBType.array().of(GeoDisplaySchema)),
    /* @prop */
    excludedInterests: NBType.array().of(NBType.enum(Interest)),
    /* @prop */
    excludedLanguages: NBType.array().of(NBType.enum(Language)),
    /* @prop */
    excludedPosts: NBType.array().of(NBType.array().of(PostSchema)),
    /* @prop */
    excludedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    excludedProfessions: NBType.array().of(NBType.enum(Profession)),
    /* @prop */
    excludedSentimentDriver: SentimentDriverSchema,
    /* @prop */
    excludedTerms: NBType.array().of(ScopedStringListSchema),
    /* @prop */
    excludedUserTags: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    followers: NBType.array().of(NBType.enum(Followers)),
    /* @prop */
    genders: NBType.array().of(NBType.enum(Gender)),
    /* @prop */
    geoFencings: NBType.array().of(NBType.array().of(GeoFencingSchema)),
    /* @prop */
    includedAuthors: NBType.array().of(AuthorSchema),
    /* @prop */
    includedBioTexts: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    includedChannels: NBType.array().of(SocialWebChannelSchema),
    /* @prop */
    includedDomains: NBType.array().of(NBType.string()),
    /* @prop */
    includedEmojis: NBType.array().of(NBType.string()).nullable(),
    /* @prop */
    includedGeographies: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    includedGeographiesSummary: NBType.array().of(NBType.array().of(GeoDisplaySchema)),
    /* @prop */
    includedInterests: NBType.array().of(NBType.enum(Interest)),
    /* @prop */
    includedLanguages: NBType.array().of(NBType.enum(Language)),
    /* @prop */
    includedPosts: NBType.array().of(NBType.array().of(PostSchema)),
    /* @prop */
    includedProductHierarchy: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    includedProfessions: NBType.array().of(NBType.enum(Profession)),
    /* @prop */
    includedSentimentDriver: SentimentDriverSchema,
    /* @prop */
    includedTerms: NBType.array().of(ScopedStringListSchema),
    /* @prop */
    includedUserTags: NBType.array().of(NBType.array().of(NBType.string())),
    /* @prop */
    lxnxFilter: LxnxFilterSchema,
    /* @prop */
    measureTypes: NBType.array().of(NBType.enum(MeasureType)),
    /* @prop */
    mediaTypes: NBType.array().of(NBType.enum(MediaType)),
    /* @prop */
    metricNames: NBType.array().of(NBType.string()),
    /* @prop */
    name: NBType.string(),
    /* @prop */
    netPromoterScores: NBType.array().of(MeasurementFilterSchema),
    /* @prop */
    organicPaidPostTypes: NBType.array().of(NBType.enum(OrganicPaidPostType)),
    /* @prop */
    ownedEarned: NBType.array().of(NBType.enum(OwnedEarned)),
    /* @prop */
    parentAnalysisType: NBType.enum(AnalysisType),
    /* @prop */
    pivotThemeInfos: NBType.array().of(ThemeInfoSchema),
    /* @prop */
    posX: NBType.number(),
    /* @prop */
    posY: NBType.number(),
    /* @prop */
    postTypeGroups: NBType.array().of(NBType.enum(PostTypeGroup)),
    /* @prop */
    precision: NBType.enum(Precision),
    /* @prop */
    primaryTerms: NBType.array().of(NBType.string()),
    /* @prop */
    removedInsights: NBType.array().of(NBType.string()),
    /* @prop */
    selectedTopic: TopicInfoSchema,
    /* @prop */
    sentiments: NBType.array().of(NBType.enum(Sentiment)),
    /* @prop */
    sizeX: NBType.number(),
    /* @prop */
    sizeY: NBType.number(),
    /* @prop */
    sources: NBType.array().of(NBType.enum(Source)),
    /* @prop */
    starRatings: NBType.array().of(MeasurementFilterSchema),
    /* @prop */
    startDatetime: NBType.number(),
    /* @prop */
    surveyScores: NBType.array().of(MeasurementFilterSchema),
    /* @prop */
    themeInfos: NBType.array().of(ThemeInfoListSchema),
    /* @prop */
    timeInterval: NBType.enum(TimeInterval),
    /* @prop */
    timeUnit: NBType.enum(MetricTimeUnit),
    /* @prop */
    topicInfos: NBType.array().of(TopicInfoSchema),
    /* @prop */
    type: NBType.enum(WidgetType),
    /* @prop */
    verifiedUsers: NBType.array().of(NBType.enum(VerifiedUsers)),
    /* @prop */
    widgetId: NBType.number()
    /* END */
  }
)
export default SocialWebTimelineComparisonWidgetSchema
export type SocialWebTimelineComparisonWidget = InferType<
  typeof SocialWebTimelineComparisonWidgetSchema
>
// RESERVE BELOW
